// 运行时配置
import { history, type RequestConfig } from '@umijs/max';
import { Button, message, Modal, Result } from 'antd';
import sha256 from 'crypto-js/sha256';
import Cookies from 'js-cookie';
import { isNumber, size } from 'lodash';
import { stringify } from 'qs';
import common from './common';
// import { languagesMapping, X_CSRF_TOKEN } from './constants';
import {
  getAdminLoggedService,
  getLoggedService,
  getMenusService,
  getRolesByUserId,
  getUMLoggedService,
} from './pages/Login/services';
import { sendDataToZCM } from './utils/utils';

declare global {
  interface Window {
    dataLayer: any[];
    dataToZCM: {
      data: {
        front_log_http: any[];
        front_log_ngportal: any[];
      };
      hasInnerVar: boolean;
    };
    menuList: any[];
  }
}

// 全局初始化数据配置，用于 Layout 用户信息和权限初始化
export async function getInitialState() {
  const currentUserType = localStorage.getItem('currentUserType');
  let loggedInfo = null;
  const { projectCode, X_CSRF_TOKEN } = common;

  // TM项目admin进入登录界面调/bcsc-service/logged
  // user登录后调/bcare/logged
  // um项目定制化logged接口
  if (projectCode === 'um' && currentUserType === 'admin') {
    loggedInfo = await getUMLoggedService();
  } else {
    if (currentUserType && currentUserType === 'admin') {
      loggedInfo = await getAdminLoggedService();
    } else {
      loggedInfo = await getLoggedService();
    }
  }

  // 如果在登录页,但是logged请求到已登录,那么就跳走
  if (window.location.pathname.includes('/login') && size(loggedInfo) > 5) {
    // history.push(
    //   currentUserType && currentUserType === 'admin' ? '/portalMgr' : '/overview',
    // );
    history.push('/');
  }

  const { _csrf, portalId = 1, userId } = loggedInfo;
  if (_csrf && _csrf.token && isNumber(portalId) && isNumber(userId)) {
    sessionStorage.setItem(X_CSRF_TOKEN, _csrf.token);
    const menus = await getMenusService(portalId);
    const [roles] = await getRolesByUserId(userId);
    return {
      ...loggedInfo,
      roles,
      menus,
    };
  }
  return {};
}

export const locale = {
  getLocale() {
    const lan = Cookies.get('ZSMART_LOCALE') || 'en';
    return common?.languagesMapping[lan];
  },
};

// 错误处理方案： 错误类型
// enum ErrorShowType {
//   SILENT = 0,
//   WARN_MESSAGE = 1,
//   ERROR_MESSAGE = 2,
//   NOTIFICATION = 3,
//   REDIRECT = 9,
// }

// 与后端约定的响应数据格式
interface ResponseStructure {
  resultCode: string;
  resultDesc: string;
  data: any;
}

window.dataToZCM = {
  data: {
    front_log_http: [],
    front_log_ngportal: [],
  },
  hasInnerVar: true,
};

// 监听接口并上报
const logHttpItem = (response: any) => {
  // 只上报url是菜单的接口，例如登录的接口不上报
  if (window?.menuList?.length > 0) {
    // 计算请求耗时
    const startTime =
      response?.config?.metadata?.startTime || new Date().getTime();
    const endTime = new Date().getTime();
    const duration = endTime - startTime;

    // 为请求匹配menuId
    const match = window.location.pathname.match(/^\/[^/]+\/(.*)/);
    const matchMenu = match ? match[1] : '';
    const menuId = window.menuList.find(
      (item) => item.url === matchMenu,
    )?.partyId;

    const httpItem = {
      payload: {
        request: {
          headers: {
            currentMenu: {
              menuId,
            },
          },
          ...(response?.status.toString().startsWith('2')
            ? {}
            : { body: response?.status }),
        },
        response: {
          timing: {
            duration,
          },
        },
      },
    };

    window.dataToZCM.data.front_log_http.push(JSON.stringify(httpItem));

    const totalLength =
      window.dataToZCM.data.front_log_http.length +
      window.dataToZCM.data.front_log_ngportal.length;
    if (totalLength > 20) {
      sendDataToZCM(20);
    }
  }
};

export const request: RequestConfig = {
  // 统一的请求设定
  timeout: 30000,
  baseURL: common.publicPath,
  validateStatus: function (status) {
    // 401是登录接口返回的, 统一到接口请求那里处理
    //  417是因为与umi的417冲突导致无法处理接口返回，统一到接口响应处理
    return status === 200 || status === 401 || status === 417 || status === 204;
  },
  errorConfig: {
    // 错误抛出
    // errorThrower: (res: ResponseStructure) => {
    //   console.log('errorThrower res', res);
    //   const { resultCode, resultDesc } = res;
    //   if (resultCode && resultCode !== '200') {
    //     const error: any = new Error(resultDesc);
    //     error.name = 'BizError';
    //     error.info = { resultDesc, resultCode };
    //     throw error; // 抛出自制的错误
    //   }
    // },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      console.log(error);
      if (opts?.skipErrorHandler) throw error;
      if (error.name === 'BizError' && error?.info) {
        const errorInfo: ResponseStructure = error.info;
        const { resultCode, resultDesc } = errorInfo;

        if (resultCode === '41300288') {
          // 客户端时间校验失败
          Modal.warning({
            footer: false,
            width: 600,
            title: 'Request Failed',
            content: (
              <Result
                status="warning"
                title={<div style={{ fontSize: '18px' }}>{resultDesc}</div>}
                extra={
                  <Button type="primary" onClick={() => location.reload()}>
                    Refresh Page
                  </Button>
                }
              />
            ),
          });
        } else {
          message.error(resultDesc);
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        logHttpItem(error.response);
        const errorMsg = error.response?.data?.message
          ? error.response?.data?.message
          : error.message;
        message.error(errorMsg);
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        logHttpItem({ status: 400, config: error?.config });
        message.error('None response! Please retry.');
      } else {
        // 发送请求时出了点问题
        logHttpItem({ status: 500, config: error?.config });
        message.error('Request error, please retry.');
      }
    },
  },
  // 请求拦截器
  requestInterceptors: [
    (config: any) => {
      // 记录请求开始时间
      config.metadata = { startTime: new Date().getTime() };
      // const token = sessionStorage.getItem(X_CSRF_TOKEN);
      // if (token) {
      //   config.headers['authtoken'] = token;
      //   config.headers[X_CSRF_TOKEN] = token;
      // }
      // return { ...config };
      const { X_CSRF_TOKEN } = common;
      let { url, ...newOptions } = config;

      // 获取token
      const acToken = sessionStorage.getItem(X_CSRF_TOKEN) || '';
      if (acToken) {
        newOptions.headers['authtoken'] = acToken;
        newOptions.headers[X_CSRF_TOKEN] = acToken;
      }
      newOptions.headers['X-Requested-With'] = 'XMLHttpRequest';
      // bcare做签名加密
      if (url.indexOf('bcare') > -1) {
        // 拼接url
        const timestamp = new Date().getTime();
        let signCode = `${timestamp}${url}`;

        // 拼接查询参数
        if (config.params && Object.keys(config.params).length > 0) {
          let queryString = stringify(config.params, { encode: false });
          signCode += `${url.indexOf('?') !== -1 ? '' : '?'}${queryString}`;
        }

        // 拼接body
        if (config.data && Object.keys(config.data).length > 0) {
          signCode += JSON.stringify(config.data).replace(
            /[^a-zA-Z\d]|null|\s+/g,
            '',
          );
        }
        
        newOptions.headers = {
          ...newOptions.headers,
          timestamp,
          signcode: sha256(`${signCode}${acToken}32BytesString`).toString(),
        };
      }

      return {
        url,
        ...newOptions,
      };
    },
  ],
  // 响应拦截器
  responseInterceptors: [
    (response: any) => {
      logHttpItem(response);

      // 拦截响应数据，进行个性化处理
      const { data, status, config } = response;
      const {
        resultCode,
        resultDesc,
        code,
        message,
        errorCode,
        errorMsg,
        successFlag,
      } = data;
      const currentPathName = window.location.pathname;
      if (resultCode && resultCode !== '200') {
        // 自制性错误
        const error: any = new Error(resultDesc);
        error.name = 'BizError';
        error.info = { resultDesc, resultCode };
        // throw error;
        return Promise.reject({
          name: 'BizError',
          info: { resultDesc, resultCode },
        });
      }
      if (
        !window.location.href.includes('/login') &&
        ((code && code === 'S-SYS-00027') ||
          (config?.url.includes('/logged') && size(data) < 5))
      ) {
        // 两种场景需要跳回登录页  (都不能在登录界面)
        // 1. 接口返回 session失效
        // 2. 在需要权限的界面访问logged,返回值不对
        const currentUserType =
          localStorage.getItem('currentUserType') || 'user';
        sessionStorage.clear();
        history.push(`/login/${currentUserType}`);
        return (
          !['', '/'].includes(currentPathName.replace('/bcsc', '')) &&
          Promise.reject({
            name: 'BizError',
            info: { resultDesc: message || 'Expired Login!', resultCode: code },
          })
        );
      }
      if (status === 417 && code && code !== '200') {
        return Promise.reject({
          name: 'BizError',
          info: { resultDesc: message, resultCode: code },
        });
      }
      if (['/bcare/bill/detail'].includes(config?.url)) {
        return data;
      }
      if (errorCode && errorCode !== '200') {
        return Promise.reject({
          name: 'BizError',
          info: { resultDesc: errorMsg, resultCode: errorMsg },
        });
      }
      if ((successFlag && successFlag === 'false') || successFlag === false) {
        return Promise.reject({
          name: 'BizError',
          info: { resultDesc: errorMsg, resultCode: errorMsg },
        });
      }
      return response;
    },
  ],
};
